import { Injectable, Inject, Optional } from '@angular/core';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  deleteToken,
  Messaging,
} from '@angular/fire/messaging';
import { getApp, FirebaseApp } from '@angular/fire/app';
import { ToastService } from '@teorema/patron';
import { environment } from '../../environments/environment';

declare const self: any;

@Injectable({
  providedIn: 'root',
})
export class FcmService {
  private messaging: Messaging | null = null;
  private fcmToken: string | null = null;

  constructor(
    @Inject('FIREBASE_MESSAGING_APP') private fcmApp: FirebaseApp,
    private toastService: ToastService
  ) {
    this.initializeMessaging();
  }

  private async initializeMessaging() {
    try {
      const supported = await isSupported();
      if (supported) {
        this.messaging = getMessaging(this.fcmApp);

        // Handle incoming messages
        onMessage(this.messaging, payload => {
          console.log('Message received:', payload);
          // Show notification to user
          if (payload.notification) {
            const notification = payload.notification;
            this.showNotification(notification.title || 'Nova notificação', {
              body: notification.body,
              icon: notification.icon || '/assets/icons/icon-72x72.png',
            });
          }
        });
      }
    } catch (error) {
      console.error('Error initializing FCM:', error);
    }
  }

  async getToken(): Promise<string | null> {
    if (!this.messaging) {
      console.warn('FCM not supported or not initialized');
      return null;
    }

    try {
      // Request notification permission if not already granted
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission not granted');
        return null;
      }

      // Get the FCM token using messagingSenderId as the vapidKey
      this.fcmToken = await getToken(this.messaging, {
        vapidKey: environment.fcmConfig.messagingSenderId,
      });

      if (!this.fcmToken) {
        console.warn('No registration token available.');
        return null;
      }

      return this.fcmToken;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  async deleteToken(): Promise<boolean> {
    if (!this.messaging || !this.fcmToken) {
      return false;
    }

    try {
      await deleteToken(this.messaging);
      this.fcmToken = null;
      return true;
    } catch (error) {
      console.error('Error deleting FCM token:', error);
      return false;
    }
  }

  private showNotification(title: string, options?: NotificationOptions) {
    if (Notification.permission === 'granted') {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          const notificationOptions: any = { ...options };
          // Only add vibrate if it's supported
          if ('vibrate' in Notification.prototype) {
            notificationOptions.vibrate = [200, 100, 200, 100, 200, 100, 200];
          }
          notificationOptions.tag = 'notification';

          registration.showNotification(title, notificationOptions);
        });
      } else {
        // Fallback for browsers that don't support service workers
        new Notification(title, options);
      }
    }
  }

  // Handle background messages (for service worker)
  public static onBackgroundMessage() {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // This will be called when a message is received while the app is in the background
        navigator.serviceWorker.addEventListener('message', event => {
          console.log('Background message received:', event.data);
          // Handle the message as needed
        });
      });
    }
  }
}
